/**
 * Vocabulary-service catalogue API.
 *
 * Unlike the rest of the app (which talks to core via the `/core/api/v1` BFF base),
 * this reads the vocabulary-service directly through the gateway's stable
 * `/vocabulary-service` prefix (StripPrefix=1 -> `/api/v1/vocabularies`). Kept separate
 * from `@/api/client` because that client is hard-wired to the core base URL.
 */

export interface VocabularyAnnotationDto {
  /** What the annotation contributes: `genes`, `titles`, or `disease`. */
  readonly type: string;
  readonly sourceUrl: string;
  readonly sha256?: string | null;
}

/**
 * One row in a vocabulary's info panel. Mirrors the backend `DisplayConfig.DetailField`.
 * `key` is a core term field (`def`, `synonyms`, `parents`, …) or a `properties` key
 * (`symbol`, `symptom`, `entrez_id`, …); the client resolves it against the core fields
 * first, then `properties`. `role` is an open semantic/label key mapped to a localized
 * label via i18n (`vocab.term.role.<role>`).
 */
export interface VocabularyDetailFieldDto {
  readonly key: string;
  readonly role: string;
  /** When `"name"`, the values are term ids to hydrate to names via bulk-get before display. */
  readonly resolve?: string | null;
  /** Vocabulary to resolve ids against (e.g. `hpo` for symptoms). */
  readonly vocabulary?: string | null;
  /** Per-value external link template; `{v}` = the field value. */
  readonly link?: string | null;
}

/**
 * Per-vocabulary presentation hints (from the catalogue) so a single generic picker +
 * info panel can render any vocabulary without per-vocab branching. Mirrors the legacy
 * PhenoTips `xTooltip`.
 */
export interface VocabularyDisplayDto {
  /** Field used as the row/header title (e.g. HPO `name`, HGNC `symbol`). */
  readonly titleField?: string | null;
  /** Optional secondary line under the title (e.g. HGNC `name`). */
  readonly subtitleField?: string | null;
  /** Term deep-link template; tokens `{id}` (full prefixed id) and `{localId}` (after `:`). */
  readonly externalLink?: string | null;
  readonly detailFields?: readonly VocabularyDetailFieldDto[] | null;
}

export interface VocabularyInfoDto {
  readonly id: string;
  readonly name?: string | null;
  readonly idPrefix?: string | null;
  /** Dated S3 snapshot the vocabulary is hosted under (e.g. `2026-05-01`) — a consistent version. */
  readonly snapshotDate?: string | null;
  /** The vocabulary's own declared release version (e.g. ORDO `owl:versionInfo`); may be absent. */
  readonly dataVersion?: string | null;
  readonly termCount: number;
  /** ISO-8601 timestamp the index was built. */
  readonly indexedAt?: string | null;
  readonly sourceUrl?: string | null;
  readonly sourceSha256?: string | null;
  readonly annotations?: readonly VocabularyAnnotationDto[] | null;
  /** Presentation hints for the term picker + info panel. */
  readonly display?: VocabularyDisplayDto | null;
}

/**
 * A vocabulary term, as returned by the search / term-detail / bulk-get endpoints.
 * Field names are snake_case (the backend `VocabTermDTO` serializes snake_case, unlike
 * the camelCase catalogue). `properties` carries the vocabulary-specific fields keyed by
 * the (suffix-stripped) source field name.
 */
export interface VocabTerm {
  readonly id: string;
  readonly name?: string | null;
  readonly def?: string | null;
  /** Alternative ids. The live BE serializes camelCase (`altIds`); the
   *  snake_case alias is kept for legacy/test payloads. */
  readonly alt_ids?: readonly string[] | null;
  readonly altIds?: readonly string[] | null;
  readonly synonyms?: readonly string[] | null;
  readonly parents?: readonly string[] | null;
  /** Curated ancestor set used for subtree filtering. Same camelCase /
   *  snake_case duality as {@link altIds}. */
  readonly term_category?: readonly string[] | null;
  readonly termCategory?: readonly string[] | null;
  readonly vocabulary?: string | null;
  readonly obsolete?: boolean | null;
  readonly properties?: Readonly<Record<string, readonly string[]>> | null;
}

/**
 * Curated body-system ancestor set on a {@link VocabTerm}. The live HPO
 * vocabulary-service serialises this as `termCategory` (camelCase); older
 * fixtures used `term_category`. Callers must use this helper rather than
 * reading the field directly so the subtree-scoped pickers don't silently
 * drop every result when the wire shape flips.
 */
export function termCategoryOf(term: VocabTerm): readonly string[] {
  return term.termCategory ?? term.term_category ?? [];
}

const VOCABULARY_BASE = '/vocabulary-service/api/v1/vocabularies';
const VOCABULARY_CATALOGUE_URL = VOCABULARY_BASE;

/** Term-picker page size — wide enough that a term matched only via a lower-ranked name still surfaces (e.g. cutaneous melanoma ranks ~53rd for "melanoma"). */
export const VOCABULARY_SEARCH_SIZE = 60;

export async function fetchVocabularies(signal?: AbortSignal): Promise<readonly VocabularyInfoDto[]> {
  const response = await fetch(VOCABULARY_CATALOGUE_URL, {
    // `?? null`: RequestInit.signal is `AbortSignal | null`, and exactOptionalPropertyTypes
    // rejects passing `undefined` for it.
    signal: signal ?? null,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Vocabulary catalogue request failed (HTTP ${String(response.status)})`);
  }
  return (await response.json()) as readonly VocabularyInfoDto[];
}

/** The search endpoint returns Spring's PagedModel ({ content, page }); we want the rows. */
interface PagedTerms {
  readonly content?: readonly VocabTerm[];
}

/**
 * Free-text type-ahead search within one vocabulary. Relevance-ranked by the service.
 * Returns at most `size` terms (default {@link VOCABULARY_SEARCH_SIZE}).
 */
export async function searchVocabularyTerms(
  vocabularyId: string,
  input: string,
  options?: { readonly size?: number; readonly signal?: AbortSignal },
): Promise<readonly VocabTerm[]> {
  const size = options?.size ?? VOCABULARY_SEARCH_SIZE;
  const url =
    `${VOCABULARY_BASE}/${encodeURIComponent(vocabularyId)}/search` +
    `?input=${encodeURIComponent(input)}&size=${String(size)}`;
  const response = await fetch(url, {
    signal: options?.signal ?? null,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (!response.ok) {
    throw new Error(`Vocabulary search failed (HTTP ${String(response.status)})`);
  }
  const data = (await response.json()) as PagedTerms | readonly VocabTerm[];
  if (Array.isArray(data)) return data as readonly VocabTerm[];
  return (data as PagedTerms).content ?? [];
}

/** Fetch a single term by id (or alt id). Returns `null` on 404. */
export async function getVocabularyTerm(
  vocabularyId: string,
  id: string,
  signal?: AbortSignal,
): Promise<VocabTerm | null> {
  const url = `${VOCABULARY_BASE}/${encodeURIComponent(vocabularyId)}/terms/${encodeURIComponent(id)}`;
  const response = await fetch(url, {
    signal: signal ?? null,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (response.status === 404) return null;
  if (!response.ok) {
    throw new Error(`Vocabulary term request failed (HTTP ${String(response.status)})`);
  }
  return (await response.json()) as VocabTerm;
}

/**
 * Children of a single term — the immediate `is-a` descendants. Used by the
 * HPO browse dialog (PTV2-1122) to lazily expand the tree on demand instead
 * of bundling a static snapshot. Returns an empty array for leaves.
 */
export async function getVocabularyTermChildren(
  vocabularyId: string,
  id: string,
  signal?: AbortSignal,
): Promise<readonly VocabTerm[]> {
  const url =
    `${VOCABULARY_BASE}/${encodeURIComponent(vocabularyId)}` +
    `/terms/${encodeURIComponent(id)}/children`;
  const response = await fetch(url, {
    signal: signal ?? null,
    headers: { Accept: 'application/json' },
    credentials: 'include',
  });
  if (response.status === 404) return [];
  if (!response.ok) {
    throw new Error(`Vocabulary children request failed (HTTP ${String(response.status)})`);
  }
  return (await response.json()) as readonly VocabTerm[];
}

/**
 * Every descendant of `rootId` (the root itself excluded), flattened and
 * de-duplicated. Walks the `is-a` children breadth-first via
 * {@link getVocabularyTermChildren}, following the DAG safely (a term reached
 * by two parents is visited once) and dropping obsolete terms.
 *
 * Used to scope the phenotype-qualifier pickers (onset, severity, and the
 * PTV2-1169 modifiers) to a bounded HPO subtree. The free-text search +
 * `term_category` filter returns nothing for these because clinical-modifier
 * terms (under HP:0012823) carry no body-system category — so we materialise
 * the subtree instead and filter it locally. PTV2-1168. These subtrees are
 * small (tens of terms across a few levels); do NOT use this for the
 * phenotypic-abnormality body-system roots, which have thousands of members.
 */
export async function fetchVocabularySubtreeTerms(
  vocabularyId: string,
  rootId: string,
  signal?: AbortSignal,
): Promise<readonly VocabTerm[]> {
  const seen = new Set<string>([rootId]);
  const collected: VocabTerm[] = [];
  let frontier: readonly string[] = [rootId];
  while (frontier.length > 0) {
    const batches = await Promise.all(
      frontier.map((id) => getVocabularyTermChildren(vocabularyId, id, signal)),
    );
    const next: string[] = [];
    for (const children of batches) {
      for (const child of children) {
        if (seen.has(child.id)) continue;
        seen.add(child.id);
        next.push(child.id);
        if (child.obsolete !== true) collected.push(child);
      }
    }
    frontier = next;
  }
  return collected;
}

/** Bulk-resolve term ids to terms (used to hydrate symptom / parent ids to names). */
export async function bulkGetVocabularyTerms(
  vocabularyId: string,
  ids: readonly string[],
  signal?: AbortSignal,
): Promise<readonly VocabTerm[]> {
  if (ids.length === 0) return [];
  const url = `${VOCABULARY_BASE}/${encodeURIComponent(vocabularyId)}/terms/bulk-get`;
  const response = await fetch(url, {
    method: 'POST',
    signal: signal ?? null,
    headers: { Accept: 'application/json', 'Content-Type': 'application/json' },
    credentials: 'include',
    body: JSON.stringify(ids),
  });
  if (!response.ok) {
    throw new Error(`Vocabulary bulk-get failed (HTTP ${String(response.status)})`);
  }
  return (await response.json()) as readonly VocabTerm[];
}
